export type PlanKey =
  | "starter_monthly"
  | "growth_monthly"
  | "team_monthly"
  | "pro_monthly";

export type LimitCode = "CLIENT_LIMIT" | "SEAT_LIMIT" | "FEATURE_RESTRICTED";

export type PlanLimitError = {
  error: "plan_limit_reached";
  code: LimitCode;
  required_plan_key: PlanKey | null;
};

export type OrgEntitlements = {
  plan_key: PlanKey;
  plan_name: string;
  is_legacy: boolean;
  max_clients: number | null;
  max_coach_seats: number | null;
  allow_custom_branding: boolean;
  allow_public_links: boolean;
  client_count: number;
  coach_seat_count: number;
  can_add_client: boolean;
  can_invite_coach: boolean;
  clients_over_limit: boolean;
  coach_seats_over_limit: boolean;
  required_plan_for_client_limit: PlanKey | null;
  required_plan_for_seat_limit: PlanKey | null;
};

export const PLAN_LABELS: Record<PlanKey, string> = {
  starter_monthly: "Starter",
  growth_monthly: "Growth",
  team_monthly: "Team",
  pro_monthly: "Pro (Legacy)",
};

export const PLAN_MONTHLY_PRICES_USD: Record<Exclude<PlanKey, "pro_monthly">, number> =
  {
    starter_monthly: 19,
    growth_monthly: 35,
    team_monthly: 75,
  };

export const canUseBranding = (entitlements?: OrgEntitlements | null) =>
  Boolean(entitlements?.allow_custom_branding);

export const canUsePublicLinks = (_entitlements?: OrgEntitlements | null) =>
  true;

export const canUsePublicIdentifier = (entitlements?: OrgEntitlements | null) =>
  entitlements?.plan_key !== "starter_monthly";

const PLAN_RANK: Record<PlanKey, number> = {
  starter_monthly: 0,
  growth_monthly: 1,
  team_monthly: 2,
  pro_monthly: 3,
};

export const isGrowthPlanOrAbove = (entitlements?: OrgEntitlements | null) => {
  if (!entitlements?.plan_key) return false;
  return PLAN_RANK[entitlements.plan_key] >= PLAN_RANK.growth_monthly;
};

export const canUseLeadForms = (entitlements?: OrgEntitlements | null) =>
  isGrowthPlanOrAbove(entitlements);

export const canUseVault = (entitlements?: OrgEntitlements | null) =>
  isGrowthPlanOrAbove(entitlements);

export const canAddClient = (entitlements?: OrgEntitlements | null) =>
  Boolean(entitlements?.can_add_client);

export const canInviteCoach = (entitlements?: OrgEntitlements | null) =>
  Boolean(entitlements?.can_invite_coach);

export const parsePlanLimitError = (error: unknown): PlanLimitError | null => {
  if (typeof error === "string") {
    const trimmed = error.trim();
    if (!trimmed.startsWith("{")) {
      return null;
    }
    try {
      return parsePlanLimitError(JSON.parse(trimmed) as unknown);
    } catch {
      return null;
    }
  }

  if (!error || typeof error !== "object") return null;
  const value = error as Partial<PlanLimitError> & { message?: unknown };
  if (value.error === "plan_limit_reached") {
    if (
      value.code !== "CLIENT_LIMIT" &&
      value.code !== "SEAT_LIMIT" &&
      value.code !== "FEATURE_RESTRICTED"
    ) {
      return null;
    }
    return {
      error: "plan_limit_reached",
      code: value.code,
      required_plan_key:
        value.required_plan_key && typeof value.required_plan_key === "string"
          ? (value.required_plan_key as PlanKey)
          : null,
    };
  }

  if (typeof value.message === "string") {
    return parsePlanLimitError(value.message);
  }

  return null;
};
